import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment-timezone';
import { GreaterThanDirective } from 'app/directives/validators/comparison/greater-than.validator';
import { Charge } from '../../../../models/entities/charge';
import { SharedModule } from '../../../shared/shared.module';
import { ComparisonValidatorsModule } from '../../../../directives/validators/comparison/comparison-validators.module';
import { CurrencyInputComponent } from '../../../shared/currency-input/currency-input.component';
import { DepositAccountSelectorComponent } from '../../shared/deposit-account-selector/deposit-account-selector.component';
import { PercentageInputComponent } from '../../../shared/percentage-input/percentage-input.component';
import { StringValidatorsModule } from '../../../../directives/validators/string/string-validators.module';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatIconModule } from '@angular/material/icon';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { CategorySelectComponent } from '../../accounting/category-select/category-select.component';
import Moment = moment.Moment;

/**
 * Manages simultaneous edits to multiple charges with temporal constraint enforcement.
 *
 * Enforces date cascade relationships across bulk operations where active < early discount < due < late fee dates.
 * When editing dates on multiple charges, preserves existing temporal relationships by filtering available
 * dates based on the most restrictive constraints across all selected charges.
 */
@Component({
    selector: 'lf-bulk-charge-edit-form',
    styleUrls: ['../../../shared/charges/charge-form/charge-form.component.scss'],
    templateUrl: './bulk-charge-edit-form.component.html',
    standalone: true,
    imports: [
        SharedModule,
        // Dependencies
        ComparisonValidatorsModule,
        CurrencyInputComponent,
        DepositAccountSelectorComponent,
        PercentageInputComponent,
        StringValidatorsModule,
        MatDatepickerModule,
        MatMomentDateModule,
        MatIconModule,
        ControlMessagesComponent,
        CategorySelectComponent,
    ],
})
export class BulkChargeEditFormComponent implements OnInit, OnDestroy
{
    @Input() organizationId: number;
    @Input() charges: Charge[] = [];
    model: UntypedFormGroup;
    minimumLatePaymentDate: Moment;
    emailInvoice = null;

    constructor(
            private _builder: UntypedFormBuilder,
            private _greaterThanDirective: GreaterThanDirective,
    ) {
    }

    get lateFees() {
        return this.model.controls['lateFees'] as UntypedFormArray;
    }

    ngOnInit() {
        this.buildModel();
    }

    /**
     * Restricts active date selection to maintain temporal ordering across all charges.
     *
     * Prevents selection of dates after any charge's due or early discount dates to ensure
     * charges remain activatable before their payment deadlines.
     */
    filterActiveAfter = (m: Moment) => {
        if (!moment.isMoment(m)) {
            return true;
        }
        const dueDate = this.model.controls['paymentDueOn'].value;
        if (moment.isMoment(dueDate)) {
            if (m.isAfter(dueDate)) {
                return false;
            }
        } else {
            // active cannot be later than the due date on any edited charge
            for (const charge of this.charges) {
                if (m.isAfter(charge.paymentDueOn)) {
                    return false;
                }
            }
        }
        const earlyDiscount = this.model.controls['earlyBefore'].value;
        if (moment.isMoment(earlyDiscount)) {
            if (m.isAfter(earlyDiscount)) {
                return false;
            }
        } else {
            // active cannot be later than the early discount date on any edited charge
            for (const charge of this.charges) {
                if (moment.isMoment(charge.earlyBefore)) {
                    if (m.isAfter(charge.earlyBefore)) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    filterDueDate = (m: Moment) => {
        if (!moment.isMoment(m)) {
            return true;
        }
        const activeDate = this.model.controls['activeAfter'].value;
        if (moment.isMoment(activeDate)) {
            if (m.isBefore(activeDate)) {
                return false;
            }
        } else {
            // due date cannot be earlier than the active date on any edited charge
            for (const charge of this.charges) {
                if (m.isBefore(charge.activeAfter)) {
                    return false;
                }
            }
        }
        const earlyDiscount = this.model.controls['earlyBefore'].value;
        if (moment.isMoment(earlyDiscount)) {
            if (m.isBefore(earlyDiscount)) {
                return false;
            }
        } else {
            // due date cannot be earlier than the early discount date on any edited charge
            for (const charge of this.charges) {
                if (moment.isMoment(charge.earlyBefore)) {
                    if (m.isBefore(charge.earlyBefore)) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    filterEarlyBefore = (m: Moment) => {
        if (!moment.isMoment(m)) {
            return true;
        }
        const activeDate = this.model.controls['activeAfter'].value;
        if (moment.isMoment(activeDate)) {
            if (m.isBefore(activeDate)) {
                return false;
            }
        } else {
            // early discount cannot be earlier than the active date on any edited charge
            for (const charge of this.charges) {
                if (m.isBefore(charge.activeAfter)) {
                    return false;
                }
            }
        }
        const dueDate = this.model.controls['paymentDueOn'].value;
        if (moment.isMoment(dueDate)) {
            if (m.isAfter(dueDate)) {
                return false;
            }
        } else {
            // early discount cannot be later than the due date on any edited charge
            for (const charge of this.charges) {
                if (m.isAfter(charge.paymentDueOn)) {
                    return false;
                }
            }
        }
        return true;
    };

    filterLateFeeApplication = (m: Moment) => {
        if (!moment.isMoment(m)) {
            return true;
        }
        const activeDate = this.model.controls['activeAfter'].value;
        if (moment.isMoment(activeDate)) {
            if (m.isBefore(activeDate)) {
                return false;
            }
        } else {
            // late fee application cannot be earlier than the active date on any edited charge
            for (const charge of this.charges) {
                if (m.isBefore(charge.activeAfter)) {
                    return false;
                }
            }
        }
        const dueDate = this.model.controls['paymentDueOn'].value;
        if (moment.isMoment(dueDate)) {
            if (m.isBefore(dueDate)) {
                return false;
            }
        } else {
            // late fee application cannot be earlier than the due date on any edited charge
            for (const charge of this.charges) {
                if (m.isBefore(charge.paymentDueOn)) {
                    return false;
                }
            }
        }
        return true;
    };

    dirtyTheForm() {
        // Dirty late fee controls.
        const lateFeeControlNames = [
            'oneTimeLateFeeApplies',
            'oneTimeLateFeeAmount',
            'recurringLateFeeBegins',
            'recurringLateFeeIntervalValue',
            'recurringLateFeeAmount',
            'category',
        ];
        const lateFees = <UntypedFormArray>this.model.controls['lateFees'];
        lateFees.controls.forEach((lateFee: UntypedFormGroup) => {
            lateFeeControlNames.forEach(lateFeeControlName => {
                lateFee.controls[lateFeeControlName].markAsDirty({});
                lateFee.controls[lateFeeControlName].updateValueAndValidity({});
            });
        });
    }

    /**
     * Transforms form data by stripping unused late fee properties based on fee type.
     *
     * One-time fees remove recurring properties and vice versa to prevent backend validation
     * conflicts when processing mixed fee type submissions.
     */
    getFormValue() {
        const charge: any = this.model.getRawValue();
        for (const prop in charge) {
            if (charge.hasOwnProperty(prop) && charge[prop] === null) {
                delete charge[prop];
            }
        }

        for (const lateFee of charge.lateFees) {
            if (lateFee.lateFeeType === 'one-time') {
                delete lateFee['recurringLateFeeType'];
                delete lateFee['recurringLateFeeBegins'];
                delete lateFee['recurringLateFeeInterval'];
                delete lateFee['recurringLateFeeIntervalValue'];
                delete lateFee['recurringLateFeeAmount'];
            } else {
                delete lateFee['oneTimeLateFeeType'];
                delete lateFee['oneTimeLateFeeApplies'];
                delete lateFee['oneTimeLateFeeAmount'];
            }
            lateFee['categoryId'] = lateFee['category'].id;
            delete lateFee['category'];
        }

        if (charge.hasOwnProperty('depositBankAccount')) {
            charge['depositBankAccountId'] = charge['depositBankAccount'].id;
            delete charge['depositBankAccount'];
        }

        if (charge.hasOwnProperty('category')) {
            charge['categoryId'] = charge['category'].id;
            delete charge['category'];
        }

        return charge;
    }

    updateLateFeeType(fee, type) {
        fee.controls['lateFeeType'].setValue(type);
        this.updateValidatorsForLateFee(fee);
    }

    removeLateFee(index) {
        const fees = <UntypedFormArray>this.model.controls['lateFees'];
        fees.removeAt(index);
    }

    clearLateFeeControls() {
        const fees = <UntypedFormArray>this.model.controls['lateFees'];
        while (fees.length !== 0) {
            fees.removeAt(0);
        }
    }

    updateValidatorsForLateFee(lateFee: UntypedFormGroup) {
        lateFee.controls['oneTimeLateFeeApplies'].clearValidators();
        lateFee.controls['oneTimeLateFeeAmount'].clearValidators();
        lateFee.controls['recurringLateFeeBegins'].clearValidators();
        lateFee.controls['recurringLateFeeIntervalValue'].clearValidators();
        lateFee.controls['recurringLateFeeAmount'].clearValidators();

        if (lateFee.controls['lateFeeType'].value === 'one-time') {
            lateFee.controls['oneTimeLateFeeApplies'].setValidators(
                    Validators.compose([
                        Validators.required,
                    ]),
            );
            lateFee.controls['oneTimeLateFeeAmount'].setValidators(
                    Validators.compose([
                        Validators.required,
                        this._greaterThanDirective.validator(0),
                    ]),
            );
        } else {
            lateFee.controls['recurringLateFeeBegins'].setValidators(
                    Validators.compose([
                        Validators.required,
                    ]),
            );
            lateFee.controls['recurringLateFeeIntervalValue'].setValidators(
                    Validators.compose([
                        Validators.required,
                        this._greaterThanDirective.validator(0),
                    ]),
            );
            lateFee.controls['recurringLateFeeAmount'].setValidators(
                    Validators.compose([
                        Validators.required,
                        this._greaterThanDirective.validator(0),
                    ]),
            );
        }

        lateFee.controls['oneTimeLateFeeApplies'].updateValueAndValidity();
        lateFee.controls['oneTimeLateFeeAmount'].updateValueAndValidity();
        lateFee.controls['recurringLateFeeBegins'].updateValueAndValidity();
        lateFee.controls['recurringLateFeeIntervalValue'].updateValueAndValidity();
        lateFee.controls['recurringLateFeeAmount'].updateValueAndValidity();

        lateFee.updateValueAndValidity();
        this.model.updateValueAndValidity();
        this.model.controls['lateFees'].updateValueAndValidity();
    }

    ngOnDestroy(): void {
        // intentionally left blank for subscription cleanup
    }

    /**
     * Creates late fee form group with conditional validation based on fee type.
     *
     * Defaults to one-time fee structure with dynamic validator switching between one-time
     * and recurring requirements triggered by type selection.
     */
    newLateFeeModel() {
        const lateFees = <UntypedFormArray>this.model.controls['lateFees'];

        const newControl = this._builder.group({
            category: [
                null,
                Validators.required,
            ],
            lateFeeType: [
                'one-time',
            ],
            oneTimeLateFeeType: [
                'flat',
            ],
            oneTimeLateFeeApplies: [
                null,
            ],
            oneTimeLateFeeAmount: [
                null,
            ],
            recurringLateFeeBegins: [
                null,
            ],
            recurringLateFeeInterval: [
                'months',
            ],
            recurringLateFeeIntervalValue: [
                1,
            ],
            recurringLateFeeType: [
                'flat',
            ],
            recurringLateFeeAmount: [
                null,
            ],
        });

        lateFees.push(newControl);

        this.updateValidatorsForLateFee(newControl);
    }

    /**
     * Build the model object. Resets the validation state of the form.
     */
    protected buildModel() {
        this.model = this._builder.group({
            reason: [''],
            depositBankAccount: [null],
            category: [null],
            title: [null],
            description: [null],
            emailAppendMessage: [null],
            chargeAmount: [null],
            activeAfter: [null],
            paymentDueOn: [null],
            earlyDiscount: [null],
            earlyBefore: [null],
            lateFees: this._builder.array(
                    [],
            ),
            removeLateFees: [false],
            removeEarlyDiscounts: [false],
            emailInvoice: [null],
        });

        // TODO: re-enable this logic if we come up with a good way to label the instructions
        // const checkForSame = [
        //     'title',
        //     'description',
        //     'emailAppendMessage',
        //     'activeAfter',
        //     'paymentDueOn',
        //     'earlyDiscount',
        //     'earlyBefore',
        //     'chargeAmount',
        //     'depositBankAccountId',
        //     'categoryId',
        // ];
        //
        // // for any field where all of the charges have the same value, use that value as the form's default value
        // for (const field of checkForSame) {
        //     const charges = this.charges.map(charge => charge[field]);
        //     const same = charges[0] && charges.every(value => {
        //         if (moment.isMoment(value)) {
        //             return value.isSame(charges[0], 'day');
        //         } else {
        //             return value === charges[0];
        //         }
        //     });
        //     if (same) {
        //         if (field === 'depositBankAccountId') {
        //             this.model.controls['depositBankAccount'].setValue(new DepositBankAccount({id: charges[0]}));
        //         } else if (field === 'categoryId') {
        //             this.model.controls['category'].setValue(new Category({id: charges[0]}));
        //         } else {
        //             this.model.controls[field].setValue(charges[0]);
        //         }
        //     }
        // }
    }
}
